import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Settings as SettingsIcon, 
  User,
  Shield,
  Save,
  Loader2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

const SettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [spendingLimit, setSpendingLimit] = useState("");
  const [saving, setSaving] = useState(false);

  // Pull the latest user record from the backend on mount so the subscription
  // status reflects any webhook upgrade that happened after the cached
  // localStorage snapshot was last written.
  useEffect(() => {
    if (refreshUser) refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    if (user?.spending_limit) {
      setSpendingLimit(user.spending_limit.toString());
    }
  }, [user]);

  const isAdmin = user?.email === "ai.manifest.official@gmail.com";
  const isPro = user?.subscription_tier === "pro" || user?.subscription_tier === "premium";

  const handleSaveLimit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/users/spending-limit`,
        { spending_limit: spendingLimit ? parseFloat(spendingLimit) : null },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success("Spending limit updated");
      await refreshUser();
    } catch (error) {
      toast.error("Failed to update spending limit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout currentPage="settings">
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-aura-text-secondary">Manage your account and preferences</p>
        </div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-aura p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center">
              <User className="w-5 h-5 text-aura-emerald" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-white">Profile</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-white/10">
              <span className="text-aura-text-secondary">Name</span>
              <span className="text-white">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-white/10">
              <span className="text-aura-text-secondary">Email</span>
              <span className="text-white">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-aura-text-secondary">Subscription</span>
              <span className={isPro ? "premium-badge" : "text-aura-text-muted"}>
                {isPro ? "AURA PRO" : "Free"}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Responsible Gambling Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-aura p-6 mb-6 border-yellow-500/20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-white">Responsible Gambling</h2>
              <p className="text-sm text-aura-text-muted">Set limits to help manage your spending</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-aura-text-secondary">Monthly Spending Limit</Label>
              <div className="flex gap-3 mt-2">
                <div className="relative flex-1 flex items-center">
                  <span className="absolute left-3 text-aura-text-muted text-base font-medium pointer-events-none z-10">$</span>
                  <Input
                    type="number"
                    value={spendingLimit}
                    onChange={(e) => setSpendingLimit(e.target.value)}
                    placeholder="50"
                    className="input-aura !pl-8"
                    min="0"
                    step="1"
                    data-testid="spending-limit-input"
                  />
                </div>
                <Button
                  onClick={handleSaveLimit}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-limit-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-aura-text-muted mt-2">
                You'll receive a warning when approaching this limit
              </p>
            </div>

            {user?.spending_limit && (
              <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-500 font-medium">
                      Monthly limit set: ${user.spending_limit}
                    </p>
                    <p className="text-xs text-aura-text-muted mt-1">
                      We'll alert you when you approach this amount
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-xl bg-red-500/5 border border-red-500/20"
        >
          <h3 className="text-sm font-medium text-red-400 mb-2">Important Notice</h3>
          <p className="text-sm text-aura-text-muted">
            This platform is an AI analysis tool for entertainment purposes. It is not affiliated with 
            or operated by The Lott or any official lottery provider. AI predictions do not guarantee 
            wins. Please gamble responsibly.
          </p>
          <a 
            href="https://www.gamblinghelponline.org.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            Need help? Visit Gambling Help Online →
          </a>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
